import Plane from "../plane/index";
import line from "../line/index";
import vector from "../vector/index";

// Projects a patient point to an image point
export function projectPatientPointToImagePlane(patientPoint, imagePlane) {
  const rowCosines = vector.convertToVector3(imagePlane.rowCosines);
  const columnCosines = vector.convertToVector3(imagePlane.columnCosines);
  const imagePositionPatient = vector.convertToVector3(
    imagePlane.imagePositionPatient
  );
  const point = patientPoint.clone().sub(imagePositionPatient);
  const x = rowCosines.dot(point) / imagePlane.columnPixelSpacing;
  const y = columnCosines.dot(point) / imagePlane.rowPixelSpacing;

  return {
    x,
    y
  };
}

// Projects an image point to a patient point
export function imagePointToPatientPoint(imagePoint, imagePlane) {
  const rowCosines = vector.convertToVector3(imagePlane.rowCosines);
  const columnCosines = vector.convertToVector3(imagePlane.columnCosines);
  const imagePositionPatient = vector.convertToVector3(
    imagePlane.imagePositionPatient
  );

  const x = rowCosines.clone().multiplyScalar(imagePoint.x);

  x.multiplyScalar(imagePlane.columnPixelSpacing);
  const y = columnCosines.clone().multiplyScalar(imagePoint.y);

  y.multiplyScalar(imagePlane.rowPixelSpacing);
  const patientPoint = x.add(y);

  patientPoint.add(imagePositionPatient);

  return patientPoint;
}

function getRectangleFromImagePlane(imagePlane) {
  // Get the points
  const topLeft = imagePointToPatientPoint(
    {
      x: 0,
      y: 0
    },
    imagePlane
  );
  const topRight = imagePointToPatientPoint(
    {
      x: imagePlane.columns,
      y: 0
    },
    imagePlane
  );
  const bottomLeft = imagePointToPatientPoint(
    {
      x: 0,
      y: imagePlane.rows
    },
    imagePlane
  );
  const bottomRight = imagePointToPatientPoint(
    {
      x: imagePlane.columns,
      y: imagePlane.rows
    },
    imagePlane
  );

  // Get each side as a vector
  const rect = {
    top: new line.Line3(topLeft, topRight),
    left: new line.Line3(topLeft, bottomLeft),
    right: new line.Line3(topRight, bottomRight),
    bottom: new line.Line3(bottomLeft, bottomRight)
  };

  return rect;
}

export function lineRectangleIntersection(line, rect) {
  const intersections = [];

  Object.keys(rect).forEach(function(side) {
    const segment = rect[side];
    const intersection = line.intersectLine(segment);

    if (intersection) {
      intersections.push(intersection);
    }
  });

  return intersections;
}

/**
 *
 * @todo 마지막단에 출력 벡터 선택 부분이 불완전 하다.(긁어온 코드가 불완전)
 * 추후, 다른 코드를 참고하거나 공부해서 수정하자.
 *
 * @export
 * @param {*} targetImagePlane
 * @param {*} referenceImagePlane
 * @returns
 */
export function planePlaneIntersection(targetImagePlane, referenceImagePlane) {
  const targetRowCosines = vector.convertToVector3(targetImagePlane.rowCosines);
  const targetColumnCosines = vector.convertToVector3(
    targetImagePlane.columnCosines
  );
  const targetImagePositionPatient = vector.convertToVector3(
    targetImagePlane.imagePositionPatient
  );
  const referenceRowCosines = vector.convertToVector3(
    referenceImagePlane.rowCosines
  );
  const referenceColumnCosines = vector.convertToVector3(
    referenceImagePlane.columnCosines
  );
  const referenceImagePositionPatient = vector.convertToVector3(
    referenceImagePlane.imagePositionPatient
  );

  // First, get the normals of each image plane
  const targetNormal = targetRowCosines.clone().cross(targetColumnCosines);
  const targetPlane = new Plane();

  targetPlane.setFromNormalAndCoplanarPoint(
    targetNormal,
    targetImagePositionPatient
  );

  const referenceNormal = referenceRowCosines
    .clone()
    .cross(referenceColumnCosines);
  const referencePlane = new Plane();

  referencePlane.setFromNormalAndCoplanarPoint(
    referenceNormal,
    referenceImagePositionPatient
  );

  const originDirection = referencePlane.clone().intersectPlane(targetPlane);
  const origin = originDirection.origin;
  const direction = originDirection.direction;

  // Calculate the longest possible length in the reference image plane (the length of the diagonal)
  const bottomRight = imagePointToPatientPoint(
    {
      x: referenceImagePlane.columns,
      y: referenceImagePlane.rows
    },
    referenceImagePlane
  );
  const distance = referenceImagePositionPatient.distanceTo(bottomRight);

  // Use this distance to bound the ray intersecting the two planes
  const linePt = new line.Line3();

  linePt.start = origin;
  linePt.end = origin.clone().add(direction.multiplyScalar(distance));

  // Find the intersections between this line and the reference image plane's four sides
  const rect = getRectangleFromImagePlane(referenceImagePlane);
  const intersections = lineRectangleIntersection(linePt, rect);

  // Return the intersections between this line and the reference image plane's sides
  // In order to draw the reference line from the target image.
  // if (intersections.length !== 2) {
  //   return;
  // }

  if (intersections.length < 2) {
    return;
  } else if (intersections.length === 2) {
    return {
      start: intersections[0],
      //end: intersections[2]
      end: intersections[1]
    };
  } else {
    const lastItemIdx = intersections.length - 1;
    return {
      start: intersections[1],
      end: intersections[lastItemIdx]
      //end: intersections[1]
    };
  }
}

/**
 * Translate a point using a rotation angle.
 * @export @public @method
 * @name rotatePoint
 *
 * @param {Object} point - `{ x, y }` in either pixel or canvas coordinates.
 * @param {Object} center - `{ x, y }` in either pixel or canvas coordinates.
 * @param {Number} angle - angle in degrees
 * @returns {Object} - `{ x, y }` new point translated
 */
export function rotatePoint(point, center, angle) {
  const angleRadians = angle * (Math.PI / 180); // Convert to radians

  const rotatedX =
    Math.cos(angleRadians) * (point.x - center.x) -
    Math.sin(angleRadians) * (point.y - center.y) +
    center.x;

  const rotatedY =
    Math.sin(angleRadians) * (point.x - center.x) +
    Math.cos(angleRadians) * (point.y - center.y) +
    center.y;

  return {
    x: rotatedX,
    y: rotatedY
  };
}
